/**
 * StaffEditorPage — verbatim port of the prototype Staff editor.
 * Sections: Profile · Account & role · Booking & availability ·
 * Locations & schedule · Services & groups · Skills & notifications.
 */
import { useState, useEffect, type ReactNode } from "react";
import { alpha, Btn, Card, Checkbox, Icon, ImageDropField, Input, Pill, T, TimeZonePicker, toIanaTimeZone, Toggle } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { TimeZoneNote, zoneLabel } from "../../shell/TimeZoneNote";
import { tzContext } from "../../lib/datetime";
import { isValidEmail, isValidPhone, normalizePhone } from "../../lib/field-validation";
import { uploadImageField } from "../../lib/media-upload";
import StaffSessions from "./StaffSessions";
import { can, listRoleNames } from "../../shell/access";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { useLocations } from "../locations/useLocations";
import { useResources, resourceKindLabel } from "../resources/useResources";
import { useServices, svcCategoryName, svcCategoryHue, svcDurationLabel, type Service } from "../services/services-data";
import { ServicePicker } from "../services/ServiceExtras";
import { useStaff, type StaffMember, type StaffGroup } from "./useStaff";
import { staffName, staffInitials } from "./StaffList";
import {
  SVC_CHANNELS, SVC_CHANNEL, WEEKDAYS, type ChannelId,
  SvcSectionHead, SvcRow, SvcSeg, SvcSelect, SvcNumField, SvcTagField,
  ExceptionList, ScheduleGrid, type WeeklyHours,
} from "../../lib/svc-editor-primitives";
import { useT } from "../../lib/i18n";

const STAFF_ROLES = listRoleNames("events");
const DEFAULT_STAFF_ROLE = STAFF_ROLES[STAFF_ROLES.length - 1] ?? "Event Coordinator";
const STAFF_STATUSES: Record<string, { label: string; labelKey: string }> = {
  active: { label: "Active", labelKey: "common.status.active" }, inactive: { label: "Inactive", labelKey: "common.status.inactive" }, "on-leave": { label: "On leave", labelKey: "staff.editorPage.onLeave" },
};

function staffWeek(days?: string[]): WeeklyHours {
  const wk: WeeklyHours = {};
  WEEKDAYS.forEach((d) => { wk[d] = (days ?? ["Mon","Tue","Wed","Thu","Fri"]).includes(d) ? [{ start: "09:00", end: "17:00" }] : []; });
  return wk;
}
function staffLoc(locationId: string, channels: string[]) {
  const schedules: Record<string, WeeklyHours> = {};
  channels.forEach((c) => { schedules[c] = staffWeek(); });
  return { locationId, schedules };
}

function newMember(): StaffMember {
  const _id = "STAFF-" + Date.now();
  return {
    id: _id, staffId: _id, firstName: "", lastName: "", name: "", email: "", phone: "", title: "", role: null,
    bio: "", photo: "", photoUrl: null, status: "active", signsIn: false,
    channels: ["appointments"], groups: [], locations: [],
    serviceGrants: [], serviceExclusions: [], skills: [], languages: [],
    department: null, category: null, tags: [],
    concurrency: 1, dailyCap: 0, timeOff: [], slug: "", linkEnabled: false, timezone: null,
    color: "#5142C7", createdAt: new Date().toISOString(), updatedAt: "Just now",
  } satisfies StaffMember;
}

function StaffStatusPill({ status }: { status: string }) {
  const t = useT();
  const map: Record<string, "success" | "neutral" | "warn"> = { active: "success", inactive: "neutral", "on-leave": "warn" };
  const meta = STAFF_STATUSES[status];
  return <Pill tone={map[status] ?? "neutral"} dot>{meta ? t(meta.labelKey, meta.label) : status}</Pill>;
}

/* ── Profile ─────────────────────────────────────────────────────────────── */
function ProfileSection({ form, set, canWrite }: { form: StaffMember; set: (p: Partial<StaffMember>) => void; canWrite: boolean }) {
  const t = useT();
  const zoneCtx = tzContext();
  /* Contact fields are VALIDATED now (2026-08-07, Pat, operator: "email and
     phone have no verification"). Shape only — no MX/carrier claim — and shown
     on blur so a half-typed address never flashes red mid-keystroke. The save
     path re-checks, so the rule cannot be walked past by never blurring. */
  const [emailErr, setEmailErr] = useState<string | null>(null);
  const [phoneErr, setPhoneErr] = useState<string | null>(null);
  /* The avatar is UPLOADED, not kept in the form (operator, same day): the
     picked file goes through uploadMediaAsset and the record stores the
     returned URL. A failed upload must not leave a photo that looks saved. */
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoErr, setPhotoErr] = useState<string | null>(null);
  const onPhoto = async (v: string) => {
    const prev = form.photo ?? "";
    set({ photo: v });                       // instant local preview
    setPhotoErr(null);
    if (!v) { set({ photoUrl: null }); return; }
    setPhotoBusy(true);
    try {
      const url = await uploadImageField(v, { purpose: "staff-photo", ownerId: form.id });
      set({ photo: url, photoUrl: url || null });
    } catch (e) {
      console.warn("staff photo upload failed:", e);
      set({ photo: prev });
      setPhotoErr(t("staff.editorPage.photoUploadFailed", "The photo wasn't uploaded — nothing was saved. Try again, or pick a smaller image."));
    } finally { setPhotoBusy(false); }
  };
  return (
    <div>
      <SvcSectionHead icon={<Icon.user size={18} />} title={t("staff.editorPage.profile", "Profile")} desc={t('staff.editorPage.desc')} />
      <SvcRow label={t("staff.editorPage.photo", "Photo")} hint={t('staff.editorPage.hint')} full>
        <ImageDropField value={form.photo} onChange={(v) => { void onPhoto(v); }} aspect={1} disabled={!canWrite || photoBusy} testid="staff-photo" placeholder={t("staff.editorPage.dragDropAPhotoOrClickToBrowse", "Drag & drop a photo, or click to browse")} />
        {photoBusy ? <div style={{ fontSize: 11.5, color: T.soft, marginTop: 6 }}>{t("staff.editorPage.photoUploading", "Uploading photo…")}</div> : null}
        {photoErr ? <div style={{ fontSize: 11.5, color: T.red1, marginTop: 6 }}>{photoErr}</div> : null}
      </SvcRow>
      <SvcRow label={t("staff.editorPage.name", "Name")}>
        <div style={{ display: "flex", gap: 10 }}>
          <Input value={form.firstName} onChange={(e) => set({ firstName: e.target.value })} placeholder={t("staff.editorPage.firstName", "First name")} readOnly={!canWrite} />
          <Input value={form.lastName}  onChange={(e) => set({ lastName: e.target.value })}  placeholder={t("staff.editorPage.lastName", "Last name")}  readOnly={!canWrite} />
        </div>
      </SvcRow>
      <SvcRow label={t("staff.editorPage.staffId", "Staff ID")} hint={t('staff.editorPage.hint2')}>
        <Input value={form.id} readOnly style={{ fontFamily: T.fontMono, maxWidth: 220 }} />
      </SvcRow>
      <SvcRow label={t("staff.editorPage.email", "Email")} hint={form.signsIn ? t("staff.editorPage.emailHintSignsIn", "Required — this is the address they sign in with.") : undefined}>
        <Input value={form.email} type="email" inputMode="email" autoComplete="email"
          onChange={(e) => { set({ email: e.target.value }); if (emailErr) setEmailErr(null); }}
          onBlur={() => { const v = (form.email ?? "").trim(); setEmailErr(!v || isValidEmail(v) ? null : t("staff.editorPage.emailInvalid", "Enter a valid email address — name@company.com.")); }}
          error={emailErr ?? undefined}
          placeholder={t("staff.editorPage.nameCompanyCom", "name@company.com")} readOnly={!canWrite} testid="staff-email" />
      </SvcRow>
      <SvcRow label={t("staff.editorPage.phone", "Phone")}>
        <Input value={form.phone ?? ""} type="tel" inputMode="tel" autoComplete="tel"
          onChange={(e) => { set({ phone: e.target.value }); if (phoneErr) setPhoneErr(null); }}
          onBlur={() => {
            const v = form.phone ?? "";
            if (!isValidPhone(v)) { setPhoneErr(t("staff.editorPage.phoneInvalid", "Enter a valid phone number — 7–15 digits, optional leading +.")); return; }
            setPhoneErr(null);
            const norm = normalizePhone(v);
            if (norm !== v) set({ phone: norm });   // store one shape, not five
          }}
          error={phoneErr ?? undefined}
          placeholder="+1 (555) 000-0000" readOnly={!canWrite} testid="staff-phone" />
      </SvcRow>
      {/* Default display zone (2026-08-07, Pat) — CanonicalStaffMember.timezone.
          Empty is a real, supported state: the person then follows the workspace
          zone, which is what every record did before this field existed. */}
      <SvcRow label={t("staff.editorPage.timeZone", "Default time zone")} hint={t("staff.editorPage.timeZoneHint", "The zone this person's appointments and schedules are read in. Leave it on the workspace zone for anyone who works where the tenant is based.")}>
        <div style={{ maxWidth: 400 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Disabled until the write path exists. The staff write schema does
                  not accept `timezone` and no column stores it, so an enabled
                  picker collected a zone, showed "Staff member saved", and lost it
                  on reload. A control that cannot persist must not look live. */}
              <TimeZonePicker value={form.timezone ?? ""} disabled testid="staff-timezone"
                placeholder={t("staff.editorPage.timeZoneEmpty", "Workspace zone")}
                onChange={(v) => set({ timezone: toIanaTimeZone(v) || null } as Partial<StaffMember>)} />
            </div>
            {canWrite && form.timezone ? (
              <Btn variant="ghost" size="sm" onClick={() => set({ timezone: null } as Partial<StaffMember>)}>{t("staff.editorPage.timeZoneClear", "Use workspace zone")}</Btn>
            ) : null}
          </div>
          <TimeZoneNote tone="panel" testid="staff-timezone-note">
            {form.timezone ? (<>
              {t("staff.editorPage.timeZoneSet", "Appointments for this person are shown in")} <b style={{ color: T.n2 }}>{zoneLabel(form.timezone)}</b>
              {form.timezone !== zoneCtx.zone ? <>{" — "}{t("staff.editorPage.timeZoneYours", "your own view is")} {zoneLabel(zoneCtx.zone)}</> : null}.
            </>) : (<>
              {t("staff.editorPage.timeZoneUnset", "No personal zone — times follow the workspace zone")} <b style={{ color: T.n2 }}>{zoneLabel(zoneCtx.zone)}</b>.
            </>)}
          </TimeZoneNote>
        </div>
      </SvcRow>
      <SvcRow label={t("staff.editorPage.jobTitle", "Job title")} hint={t('staff.editorPage.hint3')}>
        <Input value={form.title ?? ""} onChange={(e) => set({ title: e.target.value })} placeholder={t("staff.editorPage.eGSeniorPersonalBanker", "e.g. Senior personal banker")} readOnly={!canWrite} />
      </SvcRow>
      <SvcRow label={t("staff.editorPage.publicBio", "Public bio")} hint={t('staff.editorPage.hint4')} full>
        <textarea value={form.bio ?? ""} onChange={(e) => set({ bio: e.target.value })} disabled={!canWrite} rows={3} placeholder={t("staff.editorPage.aShortIntroductionCustomersSeeWhenChoosingThisPerson", "A short introduction customers see when choosing this person.")} style={{ width: "100%", border: `1px solid ${T.n4}`, borderRadius: 8, padding: "10px 12px", fontSize: 14, fontFamily: T.font, color: T.n1, resize: "vertical", background: canWrite ? T.white : T.n6 }} />
      </SvcRow>
    </div>
  );
}

/* ── Account & role ──────────────────────────────────────────────────────── */
function AccountSection({ form, set, canWrite, isNew }: { form: StaffMember; set: (p: Partial<StaffMember>) => void; canWrite: boolean; isNew: boolean }) {
  const t = useT();
  return (
    <div>
      <SvcSectionHead icon={<Icon.lock size={18} />} title={t("staff.editorPage.accountRole", "Account & role")} desc={t('staff.editorPage.desc2')} />
      <SvcRow label={t("staff.editorPage.canSignIn", "Can sign in?")} hint={t('staff.editorPage.hint5')}>
        <Toggle on={!!form.signsIn} label={form.signsIn ? t("staff.staffEditorPage.hasALogin", "Has a login") : t("staff.list.noSignIn", "No sign-in")} onChange={canWrite ? (v) => set({ signsIn: v, role: v ? (form.role ?? DEFAULT_STAFF_ROLE) : null }) : undefined} />
      </SvcRow>
      {form.signsIn ? (
        <SvcRow label={t("staff.editorPage.studioRole", "Studio role")} hint={t('staff.editorPage.hint6')}>
          <div style={{ maxWidth: 280 }}><SvcSelect value={form.role ?? DEFAULT_STAFF_ROLE} onChange={(v) => set({ role: v })} disabled={!canWrite} options={STAFF_ROLES.map((r) => ({ id: r, label: r }))} /></div>
        </SvcRow>
      ) : (
        <div style={{ marginTop: 14, display: "flex", gap: 11, padding: "13px 15px", borderRadius: 11, background: T.n6, border: `1px solid ${T.border}` }}>
          <span style={{ color: T.soft, flexShrink: 0 }}><Icon.info size={16} /></span>
          <p style={{ margin: 0, fontSize: 13, color: T.n2 }}>{t("staff.editorPage.thisPersonHasNoLoginAndWonTAppearInTeamRoles", "This person has no login and won't appear in Team & Roles. They're still fully bookable — set their channels under")}{" "}<b>{t("staff.editorPage.bookingAvailability", "Booking & availability")}</b>.</p>
        </div>
      )}
      {/* Active sessions + the account-takeover response (2026-08-07, Pat).
          A member that has not been created yet has no sessions to read — the
          op would 404 on an id the server has never seen. */}
      {isNew ? null : (
        <StaffSessions staffId={form.id} staffName={[form.firstName, form.lastName].filter(Boolean).join(" ")}
          canWrite={canWrite} signsIn={!!form.signsIn}
          onStatusChange={(status) => set({ status })} />
      )}
    </div>
  );
}

/* ── Booking & availability ──────────────────────────────────────────────── */
function BookingSection({ form, set, canWrite }: { form: StaffMember; set: (p: Partial<StaffMember>) => void; canWrite: boolean }) {
  const t = useT();
  const chan = (id: ChannelId) => (form.channels ?? []).includes(id);
  const toggleChan = (id: ChannelId) => set({ channels: chan(id) ? (form.channels ?? []).filter((c) => c !== id) : [...(form.channels ?? []), id] });
  return (
    <div>
      <SvcSectionHead icon={<Icon.calendar size={18} />} title={t("staff.editorPage.bookingAvailability", "Booking & availability")} desc={t('staff.editorPage.desc3')} />
      <SvcRow label={t("staff.editorPage.status", "Status")} hint={t('staff.editorPage.hint7')}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ minWidth: 180 }}><SvcSelect value={form.status} onChange={(v) => set({ status: v as StaffMember["status"] })} disabled={!canWrite} options={Object.entries(STAFF_STATUSES).map(([id, v]) => ({ id, label: t(v.labelKey, v.label) }))} /></div>
        </div>
      </SvcRow>
      <SvcRow label={t("staff.editorPage.bookableChannels", "Bookable channels")} hint={t('staff.editorPage.hint8')}>
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {SVC_CHANNELS.map((c) => (
            <label key={c.id} onClick={canWrite ? () => toggleChan(c.id) : undefined} style={{ display: "flex", alignItems: "center", gap: 11, padding: "10px 13px", borderRadius: 10, border: `1px solid ${chan(c.id) ? T.p2 : T.border}`, background: chan(c.id) ? T.p4 : T.white, cursor: canWrite ? "pointer" : "default" }}>
              <Checkbox checked={chan(c.id)} />
              <span style={{ width: 28, height: 28, borderRadius: 7, background: alpha(c.hue, "1f"), color: c.hue, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                {(Icon as Record<string, (p: {size:number}) => ReactNode>)[c.icon]?.({ size: 15 })}
              </span>
              <span style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{c.label}</span>
            </label>
          ))}
        </div>
      </SvcRow>
      <SvcRow label={t("staff.editorPage.bookingMode", "Booking mode")} hint={t('staff.editorPage.hint9')}>
        <SvcSeg value={(form as any).byName ? "name" : "any"} onChange={(v) => set({ ...(v === "name" ? { byName: true } : { byName: false }) } as any)} disabled={!canWrite} options={[{ id: "name", label: t("staff.editorPage.bookableByName", "Bookable by name") }, { id: "any", label: t("staff.editorPage.anyAvailablePool", "Any-available pool") }]} />
      </SvcRow>
      <SvcRow label={t("staff.editorPage.concurrency", "Concurrency")} hint={t('staff.editorPage.hint10')}>
        <SvcNumField value={form.concurrency ?? 1} onChange={(v) => set({ concurrency: v })} min={1} max={50} unit="at once" width={140} disabled={!canWrite} />
      </SvcRow>
      <SvcRow label={t("staff.editorPage.dailyBookingCap", "Daily booking cap")} hint={t('staff.editorPage.hint11')}>
        <SvcNumField value={form.dailyCap ?? 0} onChange={(v) => set({ dailyCap: v })} min={0} max={200} unit={(form.dailyCap == null || form.dailyCap === 0) ? "· no cap" : "per day"} width={160} disabled={!canWrite} />
      </SvcRow>
      <SvcRow label={t("staff.editorPage.timeOffExceptions", "Time off & exceptions")} hint={t('staff.editorPage.hint12')} full>
        <ExceptionList items={form.timeOff ?? []} onChange={(next) => set({ timeOff: next } as any)} canWrite={canWrite} />
      </SvcRow>
    </div>
  );
}

/* ── Locations & schedule ────────────────────────────────────────────────── */
function ScheduleSection({ form, set, canWrite }: { form: StaffMember; set: (p: Partial<StaffMember>) => void; canWrite: boolean }) {
  const t = useT();
  const channels = form.channels ?? [];
  const assigned: { locationId: string; schedules: Record<string, WeeklyHours> }[] = (form.locations ?? []) as any;
  const [activeLoc, setActiveLoc] = useState<string | null>(assigned[0]?.locationId ?? null);
  const [activeChan, setActiveChan] = useState<string | null>(channels[0] ?? null);

  useEffect(() => { if (!assigned.find((a) => a.locationId === activeLoc)) setActiveLoc(assigned[0]?.locationId ?? null); }, [assigned.map((a) => a.locationId).join(",")]);
  useEffect(() => { if (!channels.some((c) => c === activeChan)) setActiveChan(channels[0] ?? null); }, [channels.join(",")]);

  // Real locations (same source as the Locations list/editor).
  const { locations, loading: locsLoading } = useLocations();
  const available = locations.filter((l) => !assigned.find((a) => a.locationId === l.id));
  const addLoc = (locId: string) => { set({ locations: [...assigned, staffLoc(locId, channels.length ? channels : ["appointments"])] } as any); setActiveLoc(locId); };
  const rmLoc = (locId: string) => set({ locations: assigned.filter((a) => a.locationId !== locId) } as any);
  const cur = assigned.find((a) => a.locationId === activeLoc);
  const locName = (id: string) => locations.find((l) => l.id === id)?.name ?? id;
  const setGrid = (wk: WeeklyHours) => { if (!cur || !activeChan) return; set({ locations: assigned.map((a) => a.locationId === activeLoc ? { ...a, schedules: { ...a.schedules, [activeChan]: wk } } : a) } as any); };
  const grid = cur && activeChan ? (cur.schedules[activeChan] ?? staffWeek([])) : null;

  return (
    <div>
      <SvcSectionHead icon={<Icon.loc size={18} />} title={t("staff.editorPage.locationsSchedule", "Locations & schedule")} desc={t('staff.editorPage.desc4')} />
      {channels.length === 0 ? (
        <div style={{ marginTop: 16, padding: "16px", borderRadius: 11, background: "#FFFAED", border: "1px solid #F2E6C7", fontSize: 13, color: T.n2 }}>{t("staff.editorPage.enableAtLeastOneBookableChannelUnder", "Enable at least one bookable channel under")}{" "}<b>{t("staff.editorPage.bookingAvailability", "Booking & availability")}</b> {t("staff.editorPage.toSetASchedule", "to set a schedule.")}</div>
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "16px 0", borderTop: `1px solid ${T.border}`, alignItems: "center" }}>
            {assigned.map((a) => { const on = activeLoc === a.locationId; return (
              <span key={a.locationId} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 8px 7px 13px", borderRadius: 999, border: `1px solid ${on ? T.p1 : T.n4}`, background: on ? T.p4 : T.white, cursor: "pointer" }} onClick={() => setActiveLoc(a.locationId)}>
                <Icon.loc size={13} /><span style={{ fontSize: 13, fontWeight: 600, color: on ? T.p1 : T.n2 }}>{locName(a.locationId)}</span>
                {canWrite ? <button onClick={(e) => { e.stopPropagation(); rmLoc(a.locationId); }} style={{ border: "none", background: "transparent", color: T.n3, cursor: "pointer", display: "inline-flex" }}><Icon.x size={12} /></button> : null}
              </span>
            ); })}
            {canWrite && available.length ? (
              <select value="" onChange={(e) => e.target.value && addLoc(e.target.value)} style={{ height: 34, padding: "0 10px", border: `1px dashed ${T.borderStrong}`, borderRadius: 999, background: T.white, color: T.p1, fontSize: 13, fontWeight: 600, fontFamily: T.font, cursor: "pointer" }}>
                <option value="">{t("staff.editorPage.addLocation", "+ Add location")}</option>
                {available.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            ) : canWrite && locsLoading ? (
              <span style={{ fontSize: 13, color: T.soft }}>{t("staff.editorPage.loadingLocations", "Loading locations…")}</span>
            ) : null}
          </div>
          {!cur ? <div style={{ padding: "30px 16px", textAlign: "center", color: T.soft, border: "1.5px dashed #E5E5E5", borderRadius: 12 }}>{t("staff.editorPage.noLocationsAssignedAddOneToSetASchedule", "No locations assigned. Add one to set a schedule.")}</div> : (
            <div>
              <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${T.border}`, marginBottom: 8 }}>
                {channels.map((id) => { const c = SVC_CHANNEL(id); const on = activeChan === id; return (
                  <button key={id} onClick={() => setActiveChan(id)} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 14px", border: "none", borderBottom: `2px solid ${on ? T.p1 : "transparent"}`, background: "transparent", color: on ? T.p1 : T.n2, fontWeight: on ? 700 : 500, fontSize: 13.5, cursor: "pointer", fontFamily: T.font }}>
                    {(Icon as Record<string, (p:{size:number}) => ReactNode>)[c.icon]?.({ size: 15 })} {c.label}
                  </button>
                ); })}
              </div>
              {grid ? <ScheduleGrid schedule={grid} onChange={setGrid} canWrite={canWrite} /> : null}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Services & groups ───────────────────────────────────────────────────── */
function ServicesSection({ form, set, canWrite, groups }: { form: StaffMember; set: (p: Partial<StaffMember>) => void; canWrite: boolean; groups: StaffGroup[] }) {
  const t = useT();
  const svcStore = useServices();
  const { items: allResources } = useResources();
  const [pickSvc, setPickSvc] = useState(false);
  const [pickRes, setPickRes] = useState(false);

  const inGroup = (gid: string) => (form.groups ?? []).includes(gid);
  const toggleGroup = (gid: string) => set({ groups: inGroup(gid) ? (form.groups ?? []).filter((g) => g !== gid) : [...(form.groups ?? []), gid] });

  // Individual grants live on the CONTRACT field `serviceGrants` (CanonicalStaffMember);
  // resource authorizations are the additive `resourceIds` (declared for the staff DTO).
  const serviceIds = form.serviceGrants ?? [];
  const resourceIds = (form as { resourceIds?: string[] }).resourceIds ?? [];
  const grantedServices = svcStore.services().filter((s: Service) => serviceIds.includes(s.id));
  const grantedResources = allResources.filter((r) => resourceIds.includes(r.id));

  const addService = (s: Service) => set({ serviceGrants: [...new Set([...serviceIds, s.id])] });
  const rmService = (id: string) => set({ serviceGrants: serviceIds.filter((x) => x !== id) });
  const addResource = (r: { id: string }) => set({ resourceIds: [...new Set([...resourceIds, r.id])] } as Partial<StaffMember>);
  const rmResource = (id: string) => set({ resourceIds: resourceIds.filter((x) => x !== id) } as Partial<StaffMember>);

  const emptyBox = (msg: string) => <div style={{ padding: "20px 16px", textAlign: "center", color: T.soft, fontSize: 13, border: `1.5px dashed ${T.border}`, borderRadius: 10 }}>{msg}</div>;
  const rowStyle = { display: "flex", alignItems: "center", gap: 11, padding: "10px 12px", border: `1px solid ${T.border}`, borderRadius: 10, background: T.white } as const;
  const rmBtn = (onClick: () => void) => canWrite ? <button onClick={onClick} title={t("common.actions.remove", "Remove")} style={{ border: "none", background: "transparent", color: T.n3, cursor: "pointer", display: "inline-flex", flexShrink: 0 }}><Icon.x size={14} /></button> : null;

  return (
    <div>
      <SvcSectionHead icon={<Icon.layers size={18} />} title={t("staff.editorPage.servicesGroups", "Services & groups")} desc={t('staff.editorPage.desc5')} />
      <SvcRow label={t("staff.editorPage.groupMembership", "Group membership")} hint={t('staff.editorPage.hint13')}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {groups.length === 0 ? <div style={{ fontSize: 13, color: T.soft }}>{t("staff.editorPage.noGroupsYet", "No groups yet.")}</div> : groups.map((g) => { const on = inGroup(g.id); const hue = g.hue ?? T.p1; return (
            <button key={g.id} type="button" disabled={!canWrite} onClick={() => toggleGroup(g.id)}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "7px 13px", borderRadius: 999, border: `1px solid ${on ? hue : T.n4}`, background: on ? alpha(hue, "1a") : T.white, color: on ? hue : T.n2, fontSize: 13, fontWeight: 600, cursor: canWrite ? "pointer" : "default", fontFamily: T.font }}>
              <span style={{ width: 9, height: 9, borderRadius: 3, background: g.hue ?? T.p1 }} />{g.name}
            </button>
          ); })}
        </div>
      </SvcRow>

      <SvcRow label={t("staff.editorPage.individualServices", "Individual services")} hint={t('staff.editorPage.hint14')} full>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {grantedServices.length === 0 ? emptyBox(canWrite ? "No individually-granted services. Add one to authorize it directly." : "No individually-granted services.") : grantedServices.map((s: Service) => (
            <div key={s.id} style={rowStyle}>
              <span style={{ width: 30, height: 30, borderRadius: 7, background: alpha(svcCategoryHue(s.category), "1f"), color: svcCategoryHue(s.category), display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.layers size={15} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</div>
                <div style={{ fontSize: 11.5, color: T.soft }}>{svcCategoryName(s.category)}{s.duration ? /* TODO(i18n): replace template-literal with t('staff.editorPage.text', { ... }) */` · ${svcDurationLabel(s.duration)}` : ""}</div>
              </div>
              {s.status !== "published" ? <Pill tone="warn">{t("common.status.draft", "Draft")}</Pill> : null}
              {rmBtn(() => rmService(s.id))}
            </div>
          ))}
          {canWrite ? <div><Btn variant="secondary" size="sm" icon={<Icon.plus size={13} />} onClick={() => setPickSvc(true)}>{t("staff.editorPage.addService", "Add service")}</Btn></div> : null}
        </div>
      </SvcRow>

      <SvcRow label={t("staff.editorPage.resources", "Resources")} hint={t('staff.editorPage.hint15')} full>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {grantedResources.length === 0 ? emptyBox(canWrite ? "No resources authorized. Add rooms, equipment or devices this person can use." : "No resources authorized.") : grantedResources.map((r) => (
            <div key={r.id} style={rowStyle}>
              <span style={{ width: 30, height: 30, borderRadius: 7, background: alpha(r.color || T.p1, "1f"), color: r.color || T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.grid size={15} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</div>
                <div style={{ fontSize: 11.5, color: T.soft }}>{resourceKindLabel(r.kind, t)}{r.type ? /* TODO(i18n): replace template-literal with t('staff.editorPage.text2', { ... }) */` · ${r.type}` : ""}</div>
              </div>
              {rmBtn(() => rmResource(r.id))}
            </div>
          ))}
          {canWrite ? <div><Btn variant="secondary" size="sm" icon={<Icon.plus size={13} />} onClick={() => setPickRes(true)}>{t("staff.editorPage.addResource", "Add resource")}</Btn></div> : null}
        </div>
      </SvcRow>

      {pickSvc ? <ServicePicker title={t("staff.editorPage.grantAService", "Grant a service")} exclude={serviceIds} onPick={addService} onClose={() => setPickSvc(false)} /> : null}
      {pickRes ? <ResourcePicker title={t("staff.editorPage.authorizeAResource", "Authorize a resource")} exclude={resourceIds} onPick={addResource} onClose={() => setPickRes(false)} /> : null}
    </div>
  );
}

/* ── Resource picker (modal) — mirrors ServicePicker's browse-and-add UX ──── */
function ResourcePicker({ onPick, onClose, exclude = [], title = "Add a resource" }: {
  onPick: (r: { id: string; name: string; kind: string; type: string; color: string }) => void; onClose: () => void; exclude?: string[]; title?: string;
}) {
  const t = useT();
  const { items } = useResources();
  const [query, setQuery] = useState("");
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); }; document.addEventListener("keydown", k); return () => document.removeEventListener("keydown", k); }, [onClose]);
  const ex = new Set(exclude);
  const list = items.filter((r) => !ex.has(r.id) && (!query.trim() || `${r.name} ${resourceKindLabel(r.kind, t)} ${r.type}`.toLowerCase().includes(query.toLowerCase())));
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 240, background: "rgba(20,16,46,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title} style={{ width: 560, maxWidth: "94vw", maxHeight: "86vh", background: T.white, borderRadius: 14, boxShadow: T.shadowLg, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: T.n1, margin: 0 }}>{title}</h2>
          <button onClick={onClose} aria-label={t("common.actions.close", "Close")} style={{ border: "none", background: "transparent", cursor: "pointer", color: T.soft, display: "inline-flex" }}><Icon.x size={15} /></button>
        </div>
        <div style={{ padding: "12px 16px 8px" }}>
          <Input full leading={<Icon.search size={14} />} size="sm" autoFocus placeholder={t("staff.editorPage.searchResources", "Search resources…")} value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
          {list.length === 0 ? <div style={{ padding: 24, textAlign: "center", color: T.soft, fontSize: 13 }}>{query.trim() ? t('staff.editorPage.text3') : t('staff.editorPage.text4')}</div> :
            list.map((r) => (
              <button key={r.id} onClick={() => { onPick(r); onClose(); }} style={{ display: "flex", alignItems: "center", gap: 12, padding: 11, border: `1px solid ${T.border}`, borderRadius: 10, background: T.white, cursor: "pointer", fontFamily: T.font, textAlign: "left" }}>
                <span style={{ width: 32, height: 32, borderRadius: 7, background: alpha(r.color || T.p1, "1f"), color: r.color || T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.grid size={15} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{r.name}</div>
                  <div style={{ fontSize: 12, color: T.soft }}>{resourceKindLabel(r.kind, t)}{r.type ? ` · ${r.type}` : ""}</div>
                </div>
                <Icon.plus size={16} />
              </button>
            ))}
        </div>
      </div>
    </div>
  );
}

/* ── Skills & notifications ──────────────────────────────────────────────── */
function SkillsSection({ form, set, canWrite }: { form: StaffMember; set: (p: Partial<StaffMember>) => void; canWrite: boolean }) {
  const t = useT();
  const notify = (form as any).notify ?? {};
  const setNotify = (patch: object) => set({ notify: { ...notify, ...patch } } as any);
  const cal = (form as any).calendarSync ?? {};
  return (
    <div>
      <SvcSectionHead icon={<Icon.tags size={18} />} title={t("staff.editorPage.skillsNotifications", "Skills & notifications")} desc={t('staff.editorPage.desc6')} />
      <SvcRow label={t("staff.editorPage.skills", "Skills")} hint={t('staff.editorPage.hint16')}>
        <SvcTagField tags={(form as any).skills ?? []} onChange={(v) => set({ skills: v } as any)} disabled={!canWrite} placeholder={t("staff.editorPage.addASkill", "Add a skill…")} />
      </SvcRow>
      <SvcRow label={t("staff.editorPage.languages", "Languages")} hint={t('staff.editorPage.hint17')}>
        <SvcTagField tags={(form as any).languages ?? []} onChange={(v) => set({ languages: v } as any)} disabled={!canWrite} placeholder={t("staff.editorPage.addALanguage", "Add a language…")} />
      </SvcRow>
      <SvcRow label={t("staff.editorPage.notifications", "Notifications")} hint={t('staff.editorPage.hint18')}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {([["newBooking","New bookings"], ["changes","Changes & cancellations"], ["reminders","Upcoming reminders"]] as [string,string][]).map(([k, lbl]) => (
            <label key={k} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Toggle on={!!notify[k]} onChange={canWrite ? (v) => setNotify({ [k]: v }) : undefined} />
              <span style={{ fontSize: 13.5, color: T.n2 }}>{lbl}</span>
            </label>
          ))}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 2 }}>
            <span style={{ fontSize: 13.5, color: T.n2 }}>{t("staff.editorPage.via", "Via")}</span>
            <div style={{ width: 140 }}><SvcSelect value={notify.channel ?? "email"} onChange={(v) => setNotify({ channel: v })} disabled={!canWrite} options={[{ id: "email", label: t("staff.editorPage.email", "Email") }, { id: "sms", label: t("staff.editorPage.sms", "SMS") }, { id: "both", label: t("staff.editorPage.emailSms", "Email + SMS") }]} /></div>
          </div>
        </div>
      </SvcRow>
      <SvcRow label={t("staff.editorPage.calendarSync", "Calendar sync")} hint={t('staff.editorPage.hint19')}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Toggle on={!!cal.enabled} label={cal.enabled ? t("settings.security.on", "On") : t("account.details.off", "Off")} onChange={canWrite ? (v) => set({ calendarSync: { ...cal, enabled: v } } as any) : undefined} />
          {cal.enabled ? <div style={{ width: 160 }}><SvcSelect value={cal.provider ?? "google"} onChange={(v) => set({ calendarSync: { ...cal, provider: v } } as any)} disabled={!canWrite} options={[{ id: "google", label: t("staff.editorPage.googleCalendar", "Google Calendar") }, { id: "outlook", label: t("staff.editorPage.outlook", "Outlook") }, { id: "ical", label: t("staff.editorPage.icalFeed", "iCal feed") }]} /></div> : null}
        </div>
      </SvcRow>
    </div>
  );
}

/* ═══ Main editor ═══════════════════════════════════════════════════════════ */
export default function StaffEditorPage() {
  const t = useT();
  const nav = useNav();
  const store = useStaff();
  const isNew    = !!(nav.params?.new);
  const viewOnly = !!(nav.params?.view);
  const id       = nav.params?.id as string | undefined;
  const canWrite = !viewOnly && can("team.write");

  const [form, setForm] = useState<StaffMember>(() => {
    if (isNew || !id) return newMember();
    return store.members.find((m) => m.id === id) ?? newMember();
  });
  const set = (patch: Partial<StaffMember>) => setForm((f) => ({ ...f, ...patch }));
  const [section, setSection] = useState("profile");

  const SECTIONS = [
    { id: "profile",  label: t("staff.editorPage.profile", "Profile"),             icon: <Icon.user size={16} /> },
    { id: "account",  label: t("staff.editorPage.accountRole", "Account & role"),       icon: <Icon.lock size={16} /> },
    { id: "booking",  label: t("staff.editorPage.bookingAvailability", "Booking & availability"), icon: <Icon.calendar size={16} />, badge: (form.channels ?? []).length },
    { id: "schedule", label: t("staff.editorPage.locationsSchedule", "Locations & schedule"), icon: <Icon.loc size={16} />, badge: (form.locations ?? []).length },
    { id: "services", label: t("staff.editorPage.servicesGroups", "Services & groups"),    icon: <Icon.layers size={16} />, badge: (form.serviceGrants ?? []).length + ((form as { resourceIds?: string[] }).resourceIds ?? []).length },
    { id: "skills",   label: t("staff.editorPage.skillsNotifications", "Skills & notifications"), icon: <Icon.tags size={16} /> },
  ];

  const save = async () => {
    if (!form.firstName.trim() && !form.lastName.trim()) { toast({ tone: "warn", title: t("staff.editorPage.addANameFirst", "Add a name first") }); setSection("profile"); return; }
    /* Contact validation is enforced HERE too, not only on blur (2026-08-07,
       Pat): a field that is never focused never blurs, and "asdf" as an email
       used to save cleanly. An address is REQUIRED for someone who signs in —
       it is the credential — and optional but well-formed for everyone else. */
    const email = (form.email ?? "").trim();
    if (form.signsIn && !email) {
      toast({ tone: "warn", title: t("staff.editorPage.emailRequired", "Add an email address"), subtitle: t("staff.editorPage.emailRequiredSub", "Someone who can sign in needs the address they sign in with.") });
      setSection("profile"); return;
    }
    if (email && !isValidEmail(email)) {
      toast({ tone: "warn", title: t("staff.editorPage.emailInvalidToast", "Check the email address"), subtitle: t("staff.editorPage.emailInvalid", "Enter a valid email address — name@company.com.") });
      setSection("profile"); return;
    }
    if (!isValidPhone(form.phone ?? "")) {
      toast({ tone: "warn", title: t("staff.editorPage.phoneInvalidToast", "Check the phone number"), subtitle: t("staff.editorPage.phoneInvalid", "Enter a valid phone number — 7–15 digits, optional leading +.") });
      setSection("profile"); return;
    }
    // Commit the EFFECTIVE role (Greg T1440): a sign-in staff member always
    // resolves to the role the dropdown shows (form.role, or the default it
    // falls back to when unset) — the select can never present an empty value,
    // so a guard rejecting null was blocking saves the UI can't produce.
    const toSave = { ...form, email, phone: normalizePhone(form.phone ?? ""), role: form.signsIn ? (form.role ?? DEFAULT_STAFF_ROLE) : null };
    try {
      await store.upsertMember(toSave);
      toast({ tone: "success", title: isNew ? "Staff member created" : "Staff member saved" });
      nav.go("staff");
    } catch (e) {
      console.warn("staff save failed:", e);
      toast({ tone: "danger", title: t("staff.editorPage.couldnTSaveStaffMember", "Couldn't save staff member"), subtitle: t("staff.editorPage.nothingWasSavedYourEditsAreStillHereRetryInAMoment", "Nothing was saved — your edits are still here. Retry in a moment.") });
    }
  };

  return (
    <Screen rail="staff">
      <PageHeader
        title={isNew ? t("staff.list.newStaffMember", "New staff member") : (staffName(form) || t("staff.staffEditorPage.editStaffMember", "Edit staff member"))}
        subtitle={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><StaffStatusPill status={form.status} /> · {form.id}{form.signsIn ? /* TODO(i18n): replace template-literal with t('staff.editorPage.text5', { ... }) */` · ${form.role ?? DEFAULT_STAFF_ROLE}` : t('staff.editorPage.text6')}</span>}
        breadcrumb={<span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("staff")}><Icon.arrow dir="left" size={11} /> {t("staff.editorPage.staff", "Staff")}</span>}
        actions={viewOnly ? <Btn icon={<Icon.edit size={14} />} onClick={() => nav.go("staff.edit", { id: form.id })}>{t("common.actions.edit", "Edit")}</Btn> : <>
          <Btn variant="secondary" onClick={() => nav.go("staff")}>{t("common.actions.cancel", "Cancel")}</Btn>
          <Btn icon={<Icon.check size={14} />} onClick={save}>{isNew ? t("staff.staffEditorPage.create", "Create") : t("common.actions.save", "Save")}</Btn>
        </>}
      />
      <div style={{ flex: 1, overflow: "hidden", display: "flex", minHeight: 0 }}>
        <nav style={{ width: 232, flexShrink: 0, borderRight: `1px solid ${T.border}`, padding: "18px 14px", overflow: "auto", background: T.canvas }}>
          {SECTIONS.map((s) => { const on = section === s.id; return (
            <button key={s.id} onClick={() => setSection(s.id)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 11, padding: "10px 12px", marginBottom: 4, borderRadius: 9, border: "none", background: on ? T.white : "transparent", color: on ? T.p1 : T.n2, fontWeight: on ? 700 : 500, fontSize: 13.5, cursor: "pointer", fontFamily: T.font, boxShadow: on ? T.shadowSm : "none", textAlign: "left" }}>
              <span style={{ color: on ? T.p1 : T.soft, display: "inline-flex" }}>{s.icon}</span>
              <span style={{ flex: 1 }}>{s.label}</span>
              {s.badge ? <span style={{ fontSize: 11, fontWeight: 700, color: on ? T.p1 : T.soft, background: on ? T.p4 : T.n6, borderRadius: 999, padding: "1px 7px" }}>{s.badge}</span> : null}
            </button>
          ); })}
          <div style={{ marginTop: 16, padding: "12px", borderRadius: 10, background: T.white, border: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: T.soft, marginBottom: 8 }}>{t("staff.editorPage.status", "Status")}</div>
            <SvcSelect value={form.status} onChange={(v) => set({ status: v as StaffMember["status"] })} disabled={!canWrite} options={Object.entries(STAFF_STATUSES).map(([id, v]) => ({ id, label: t(v.labelKey, v.label) }))} />
          </div>
        </nav>
        <div style={{ flex: 1, overflow: "auto", padding: "24px 28px 60px", minWidth: 0 }}>
          <div style={{ maxWidth: 760 }}>
            {section === "profile"  && <ProfileSection  form={form} set={set} canWrite={canWrite} />}
            {section === "account"  && <AccountSection  form={form} set={set} canWrite={canWrite} isNew={isNew} />}
            {section === "booking"  && <BookingSection  form={form} set={set} canWrite={canWrite} />}
            {section === "schedule" && <ScheduleSection form={form} set={set} canWrite={canWrite} />}
            {section === "services" && <ServicesSection form={form} set={set} canWrite={canWrite} groups={store.groups} />}
            {section === "skills"   && <SkillsSection   form={form} set={set} canWrite={canWrite} />}
          </div>
        </div>
      </div>
    </Screen>
  );
}
